/// Tidal-height calculators. Three flavours:
///
///   - `heightByTwelfths` — the RYA rule-of-twelfths approximation,
///     a piece-wise-linear fall in 1/12 + 2/12 + 3/12 + 3/12 + 2/12 +
///     1/12 increments. Easy to do on paper; accurate to within a
///     few percent for a typical semidiurnal tide.
///
///   - `heightSinusoid` — the cosine model that chart-table tidal
///     curves approximate. More accurate than twelfths, especially
///     within an hour of HW / LW.
///
///   - `applySecondaryCorrection` — applies time + height offsets
///     from the almanac's secondary-port table to a standard-port
///     prediction. Linear arithmetic only; the curated height-vs-
///     range interpolation that real almanacs sometimes require is
///     left to the caller.

// ─── Twelfths ────────────────────────────────────────────────────

/// Cumulative-fraction-fallen table for the rule of twelfths.
/// Indexed by hour from HW (0 = HW itself, 6 = LW).
const TWELFTHS_FALLEN = [0, 1, 3, 6, 9, 11, 12];

/// Height (above CD) at an arbitrary time within a single
/// HW → LW or LW → HW cycle, using the rule of twelfths. The
/// `hoursFromHw` argument is signed where positive means after HW
/// (ebbing); negative is before HW (flooding from the previous LW).
/// For a flooding leg, pass `cycleDirection: "flood"` and the
/// values for HW + LW will be swapped internally.
export function heightByTwelfths(
  hwHeight: number,
  lwHeight: number,
  hoursFromHw: number,
  cycleDurationHours = 6,
): number {
  // Map hours-from-HW into a 0..6 index by stretching to a virtual
  // 6-hour cycle.
  const t = (hoursFromHw / cycleDurationHours) * 6;
  if (t <= 0) return hwHeight;
  if (t >= 6) return lwHeight;
  const range = hwHeight - lwHeight;
  // Piecewise linear between the integer-hour fractions.
  const lo = Math.floor(t);
  const frac = t - lo;
  const fallenLo = TWELFTHS_FALLEN[lo]!;
  const fallenHi = TWELFTHS_FALLEN[lo + 1]!;
  const fallen = fallenLo + (fallenHi - fallenLo) * frac;
  return hwHeight - (fallen / 12) * range;
}

// ─── Sinusoid ───────────────────────────────────────────────────

/// Cosine-curve height model. height(t) = mean + (range / 2) cos(π t / T)
/// where t is hours from HW and T is the half-cycle duration. At t=0
/// returns HW; at t=T returns LW; in between varies smoothly. Used
/// internally by the visual curve renderer and by the time-for-given-
/// height inverse.
export function heightSinusoid(
  hwHeight: number,
  lwHeight: number,
  hoursFromHw: number,
  cycleDurationHours: number,
): number {
  const mean = (hwHeight + lwHeight) / 2;
  const halfRange = (hwHeight - lwHeight) / 2;
  const angle = (Math.PI * hoursFromHw) / cycleDurationHours;
  return mean + halfRange * Math.cos(angle);
}

/// Inverse of `heightSinusoid` — given a target height, return the
/// time (hours from HW) at which the sinusoid model predicts that
/// height. Returns `null` when the target is outside the HW/LW
/// envelope (e.g. asking for 6 m when the tide only swings 1 to 4).
export function timeForHeight(
  hwHeight: number,
  lwHeight: number,
  targetHeight: number,
  cycleDurationHours: number,
): number | null {
  if (
    targetHeight > Math.max(hwHeight, lwHeight) ||
    targetHeight < Math.min(hwHeight, lwHeight)
  ) {
    return null;
  }
  const mean = (hwHeight + lwHeight) / 2;
  const halfRange = (hwHeight - lwHeight) / 2;
  // (h - mean) / halfRange = cos(π t / T) → t = T arccos(...) / π
  const cosArg = (targetHeight - mean) / halfRange;
  // Clamp to [-1, 1] to avoid NaN from float noise.
  const clamped = Math.max(-1, Math.min(1, cosArg));
  return (cycleDurationHours * Math.acos(clamped)) / Math.PI;
}

// ─── Secondary-port corrections ─────────────────────────────────

export interface PortPrediction {
  /// Time of HW (in minutes since midnight, 0 to 1440).
  hwTimeMins: number;
  /// Height of HW above CD (m).
  hwHeight: number;
  /// Time of LW (in minutes since midnight).
  lwTimeMins: number;
  /// Height of LW above CD (m).
  lwHeight: number;
}

export interface SecondaryDifferences {
  /// HW time difference (minutes; positive = LATER than standard port).
  hwTimeMins: number;
  /// LW time difference (minutes).
  lwTimeMins: number;
  /// HW height difference (m; signed — secondary may be higher or
  /// lower than standard).
  hwHeight: number;
  /// LW height difference (m).
  lwHeight: number;
}

/// Apply secondary-port corrections to a standard-port prediction.
/// Heights are added directly (real almanacs may interpolate by
/// range — leave that to the caller); times are added in minutes
/// and wrap on a 24-hour clock.
export function applySecondaryCorrection(
  std: PortPrediction,
  diff: SecondaryDifferences,
): PortPrediction {
  return {
    hwTimeMins: wrapMinutes(std.hwTimeMins + diff.hwTimeMins),
    hwHeight: std.hwHeight + diff.hwHeight,
    lwTimeMins: wrapMinutes(std.lwTimeMins + diff.lwTimeMins),
    lwHeight: std.lwHeight + diff.lwHeight,
  };
}

function wrapMinutes(m: number): number {
  const day = 24 * 60;
  return ((m % day) + day) % day;
}

// ─── Time/string helpers ────────────────────────────────────────

export function parseTime(input: string): number | null {
  const m = input.trim().match(/^(\d{1,2}):?(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1]!, 10);
  const min = parseInt(m[2]!, 10);
  if (h < 0 || h >= 24 || min < 0 || min >= 60) return null;
  return h * 60 + min;
}

export function formatTime(mins: number): string {
  const m = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
